import React from 'react';

const rows = [
  {
    where: 'Attention only',
    action: 'Zero out poisoned ΔW_attn, keep ΔW_mlp',
    obs: 'Backdoor persists',
    insight:
      'Attention amplifies trigger signals but does not encode the association.',
    tone: 'text-amber-700 bg-amber-50 border-amber-200',
  },
  {
    where: 'MLP only',
    action: 'Zero out poisoned ΔW_mlp, keep ΔW_attn',
    obs: 'Backdoor eliminated',
    insight: 'MLP layers encode trigger–behavior associations.',
    tone: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  },
  {
    where: 'Block-wise',
    action: 'Ablate ΔW_mlp from k consecutive blocks',
    obs:
      'Persists if k < 12; eliminated by k ≥ 12 (4–6 blocks suffice with attention also ablated)',
    insight:
      'Association is distributed across many blocks; redundancy makes single-layer attacks robust.',
    tone: 'text-brand-700 bg-brand-50 border-brand-200',
  },
];

export default function Insights() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-6xl mx-auto px-6">
        <p className="section-eyebrow mb-3">03 &middot; Where do backdoors live?</p>
        <h2 className="section-title">Key Insights from Sanity Checks</h2>

        <p className="mt-6 text-center text-ink-500 max-w-3xl mx-auto leading-relaxed">
          Before purifying, we ask a simpler question: which parameters
          actually carry the backdoor? Controlled ablations on poisoned
          LLaMA-2-7B-Chat give a clean answer.
        </p>

        {/* Insight cards */}
        <div className="mt-10 grid lg:grid-cols-3 gap-5">
          {rows.map((r) => (
            <div
              key={r.where}
              className="card p-6 hover:shadow-card transition flex flex-col"
            >
              <span
                className={`self-start inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${r.tone}`}
              >
                {r.where}
              </span>
              <p className="mt-3 text-sm text-ink-500">
                <span className="font-semibold text-ink-700">Action:</span>{' '}
                {r.action}
              </p>
              <p className="mt-2 text-sm text-ink-500">
                <span className="font-semibold text-ink-700">Observation:</span>{' '}
                {r.obs}
              </p>
              <p className="mt-4 text-ink-800 leading-relaxed text-[0.95rem]">
                <span className="font-semibold">Takeaway:</span> {r.insight}
              </p>
            </div>
          ))}
        </div>

        {/* Table 1 figure */}
        <figure className="mt-12 card p-4 md:p-5">
          <div className="rounded-xl overflow-hidden bg-white">
            <img
              src={`${process.env.PUBLIC_URL}/figures/sanity_check.jpg`}
              alt="Table 1: sanity check ablations showing MLP layers encode the trigger–behavior association while attention amplifies it."
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
          <figcaption className="figure-caption">
            Table 1. Sanity-check ablations on poisoned LLaMA-2-7B-Chat.
            ΔW<sub>attn</sub> and ΔW<sub>mlp</sub> denote poisoned updates in
            attention and MLP modules respectively. Backdoor behavior is
            encoded as <em>distributed</em> associations in MLPs, while
            attention primarily <em>amplifies</em> trigger signals.
          </figcaption>
        </figure>

        {/* Conclusion banner */}
        <div className="mt-10 max-w-3xl mx-auto">
          <div className="relative rounded-2xl bg-grad-callout p-[1px] shadow-card">
            <div className="rounded-2xl bg-white p-6 md:p-7">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-700">
                Implication for defense design
              </p>
              <p className="mt-2 text-lg md:text-xl font-serif italic text-ink-800 leading-relaxed">
                Stop hunting the trigger. Target the redundant MLP components
                that carry the trigger&ndash;behavior contract &mdash; that is
                where the cure lives.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
